import AppConfigs from 'general/constants/AppConfigs';
import axiosClient from './axiosClient';

const rideRequest = (method, url, data, config = {}) => axiosClient({
  method,
  url: `/fe/rides${url}`,
  data,
  baseURL: `${AppConfigs.env.baseUrl}api/v1`,
  ...config,
});

// These endpoints live beside (rather than under) the ride namespace. They
// are still the authenticated customer APIs used by the mobile app for the
// separate RideWallet balance and BIDV top-up flow.
const customerRequest = (method, url, data, config = {}) => axiosClient({
  method,
  url,
  data,
  baseURL: `${AppConfigs.env.baseUrl}api/v1`,
  ...config,
});

const LOCATION_CACHE_LIMIT = 300;
const locationCache = new Map();
const locationInFlight = new Map();

const normalizeLocationText = (value) => String(value || '')
  .trim()
  .replace(/\s+/g, ' ')
  .toLocaleLowerCase('vi-VN');

const locationCoordinateKey = (value, digits) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(digits) : '';
};

const cachedLocationRequest = (key, ttlMs, loader) => {
  const now = Date.now();
  const cached = locationCache.get(key);
  if (cached && cached.expiresAt > now) {
    locationCache.delete(key);
    locationCache.set(key, cached);
    return Promise.resolve(cached.value);
  }
  if (cached) locationCache.delete(key);

  const pending = locationInFlight.get(key);
  if (pending) return pending;

  const request = Promise.resolve()
    .then(loader)
    .then((value) => {
      locationCache.set(key, { value, expiresAt: Date.now() + ttlMs });
      while (locationCache.size > LOCATION_CACHE_LIMIT) {
        locationCache.delete(locationCache.keys().next().value);
      }
      return value;
    })
    .finally(() => locationInFlight.delete(key));
  locationInFlight.set(key, request);
  return request;
};

const customerRideApi = {
  getStatus: () => rideRequest('get', '/status'),
  getProfile: () => customerRequest('get', '/fe/profile'),
  topUpRideWallet: (amount) => customerRequest('post', '/fe/topup', {
    amount,
    fundingSource: 'BIDV',
    currency: 'VND',
    walletScope: 'RIDE',
  }),
  getProvinces: () => rideRequest('get', '/locations/provinces'),
  getDistricts: (provinceCode) => rideRequest('get', `/locations/provinces/${provinceCode}/districts`),
  getWards: (provinceCode, districtCode) => rideRequest('get', `/locations/provinces/${provinceCode}/districts/${districtCode}/wards`),
  getCommunes: (provinceCode) => rideRequest('get', `/locations/provinces/${provinceCode}/communes`),
  autocomplete: (params) => {
    const key = [
      'autocomplete', normalizeLocationText(params?.text),
      locationCoordinateKey(params?.lat, 3), locationCoordinateKey(params?.lng, 3),
    ].join(':');
    return cachedLocationRequest(
      key,
      5 * 60 * 1000,
      () => rideRequest('get', '/locations/geocoding/autocomplete', undefined, { params }),
    );
  },
  resolvePlace: (refId) => cachedLocationRequest(
    `place:${String(refId || '').trim()}`,
    60 * 60 * 1000,
    () => rideRequest('get', '/locations/geocoding/place', undefined, { params: { refId } }),
  ),
  // The customer ride route is the same VietMap-backed route used by mobile
  // and accepts `lat` / `lng`.
  reverseGeocode: ({ lat, lng }) => cachedLocationRequest(
    `reverse:${locationCoordinateKey(lat, 4)}:${locationCoordinateKey(lng, 4)}`,
    5 * 60 * 1000,
    () => rideRequest('get', '/locations/geocoding/reverse', undefined, {
      params: { lat, lng },
    }),
  ),
  searchTrips: (payload) => rideRequest('post', '/marketplace/trips/search', payload),
  // A scheduled ride is a customer demand published to drivers. It is not a
  // trip search or an immediate booking.
  previewScheduledDemandFare: (payload) => rideRequest('post', '/demands/preview-fare', payload),
  createScheduledDemand: (payload) => rideRequest('post', '/demands', payload),
  getMyScheduledDemands: () => rideRequest('get', '/demands/mine'),
  cancelScheduledDemand: (demandId, reason) => rideRequest('post', `/demands/${demandId}/cancel`, { reason }),
  browseTrips: (params = {}) => rideRequest('get', '/marketplace/trips', undefined, { params }),
  getPopularDestinations: () => rideRequest('get', '/marketplace/popular-destinations'),
  createQuote: (tripId, payload) => rideRequest('post', `/marketplace/trips/${tripId}/quote`, payload),
  getPublicPromoCodes: () => rideRequest('get', '/promo-codes/public'),
  checkPromoCode: (payload) => rideRequest('post', '/promo-codes/check', payload),
  createBooking: (payload, idempotencyKey) => rideRequest('post', '/bookings', payload, {
    headers: { 'Idempotency-Key': idempotencyKey },
  }),
  getMyBookings: () => rideRequest('get', '/bookings'),
  // Driver-owned management list (separate from the public marketplace list
  // used by the customer-facing "Tài xế đăng" tab).
  getMyDriverTrips: (params = {}) => rideRequest('get', '/trips', undefined, { params }),
  getMyDriverTrip: (tripId) => rideRequest('get', `/trips/${tripId}`),
  getBooking: (bookingId) => rideRequest('get', `/bookings/${bookingId}`),
  getTripProgress: (bookingId) => rideRequest('get', `/bookings/${bookingId}/trip-progress`),
  cancelBooking: (bookingId, payload) => rideRequest('post', `/bookings/${bookingId}/cancel`, payload),
  getReview: (bookingId) => rideRequest('get', `/bookings/${bookingId}/review`),
  submitReview: (bookingId, payload) => rideRequest('post', `/bookings/${bookingId}/review`, payload),
  // Keep the customer role explicit for accounts which also drive.
  syncConversations: () => rideRequest('post', '/conversations/sync'),
  getConversations: (role = 'CUSTOMER') => rideRequest('get', '/conversations', undefined, {
    params: { role },
  }),
  getConversationMessages: (conversationId, params = {}) => rideRequest(
    'get', `/conversations/${conversationId}/messages`, undefined, { params },
  ),
  sendConversationMessage: (conversationId, payload) => rideRequest(
    'post', `/conversations/${conversationId}/messages`, payload,
  ),
  markConversationRead: (conversationId) => rideRequest('post', `/conversations/${conversationId}/read`),
  // The app merges the persistent EVG support thread into the ride inbox.
  // Keep these calls in the same authenticated ride namespace so the web
  // inbox reads and writes the exact same conversation as mobile.
  getSupportConversation: () => rideRequest('get', '/support/conversation'),
  getSupportConversationMessages: (conversationId, params = {}) => rideRequest(
    'get', `/support/conversations/${conversationId}/messages`, undefined, { params },
  ),
  sendSupportMessage: (conversationId, payload) => rideRequest(
    'post', `/support/conversations/${conversationId}/messages`, payload,
  ),
  markSupportConversationRead: (conversationId) => rideRequest('post', `/support/conversations/${conversationId}/read`),
};

export default customerRideApi;
